import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useSections } from "../api/use-sections";
import { useSchedule } from "../api/use-schedules";
import type { Section } from "../types";
import { SectionsTable } from "./sections-table";

interface SectionsTabProps {
  scheduleId: string;
  /** Forwarded to {@link SectionsTable} so rows can open the roster panel. */
  onSectionClick?: (section: Section) => void;
}

/**
 * Schedule Home → Sections tab. Fetches the section list and renders it via
 * {@link SectionsTable}. The schedule query is read alongside so the table
 * can format times in the schedule's local timezone instead of UTC; we tag
 * the fetch with the same `enabled` gate as the calendar tab so a stale or
 * still-loading schedule never accidentally renders timestamps as UTC.
 */
export function SectionsTab({ scheduleId, onSectionClick }: SectionsTabProps) {
  const sectionsQuery = useSections(scheduleId);
  const scheduleQuery = useSchedule(scheduleId);
  const timeZone = scheduleQuery.data?.timeZone ?? "UTC";

  if (sectionsQuery.isLoading || scheduleQuery.isLoading) {
    return (
      <div
        data-testid="sections-tab-loading"
        className="py-16 flex justify-center"
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (sectionsQuery.isError) {
    return (
      <div
        data-testid="sections-tab-error"
        className="bg-surface border border-red-500/30 rounded-lg p-6 flex items-center justify-between gap-4"
      >
        <p className="text-sm text-red-400">
          {sectionsQuery.error instanceof Error
            ? sectionsQuery.error.message
            : "Failed to load sections."}
        </p>
        <button
          type="button"
          onClick={() => sectionsQuery.refetch()}
          data-testid="sections-tab-retry"
          className="bg-surface-2 hover:bg-surface-2/80 text-white rounded-md px-3 py-1.5 text-[13px] font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div data-testid="schedule-tab-sections-content">
      <SectionsTable
        sections={sectionsQuery.data ?? []}
        timeZone={timeZone}
        onSectionClick={onSectionClick}
      />
    </div>
  );
}
